import { fork, call, put, select, take } from 'redux-saga/effects';

import { getUserDetail } from './clapi';
import { getRecentAddressSearches, getRecentParcelSearches, getRecentTitleSearches } from './msgApi';
import {
    getRecentAddressSearches as getRecentAddressSearchesAction,
    getRecentParcelSearches as getRecentParcelSearchesAction,
    getRecentTitleSearches as getRecentTitleSearchesAction,
} from '../actions/msgApi';
import { LD_INIT } from '../actions/launchDarkly';
import { SEGMENT_INIT } from '../actions/segment';
import { initializeLD } from './launchDarkly';
import { initializeSegment } from './segment';
import { getNewUserActivityFeeds as getNewUserActivityFeedsAction } from '../actions/favourites';
import { getNewUserActivityFeeds as getNewUserActivityFeedsSaga } from './favourites';
import { isNZ } from '../constants/crux';
import initApm from './apmRum';
import { GET_USER_DETAILS_FAIL, GET_USER_DETAILS_SUCCESS } from '../actions/claud';
import { LA_ACTION } from '../actions/linkedAccounts';
import { selectSelectedAccount } from '../selectors/linkedAccount';
import { REHYDRATE } from 'redux-persist';

export const selectClaudUser = state => state.claud.get('user');

// Ensures non-critical sagas (recent-search, notifications, user/summary) do not fire
// until the full init sequence completes AND an account is selected.
//
// Phase 1 – wait for FETCH_ACCOUNTS_SUCCESS (downstream of PUBLIC_CONFIGURATION_INIT_SUCCESS):
//   1. Timeouts config is loaded (/ui/configuration responded)
//   2. Session is validated (accounts API requires auth)
//   3. Account selection state is fresh (not stale REHYDRATE data)
//
// Phase 2 – ensure an account is selected:
//   Single-account users are auto-selected in the reducer.
//   Multi-account users may have a previously-selected account restored via REHYDRATE,
//   or must pick an account on the LinkedAccounts page (SELECT_ACCOUNT).
export function* waitForInitComplete() {
    yield take(LA_ACTION.FETCH_ACCOUNTS_SUCCESS);

    let { selectedClAppAccountUserGuid } = yield select(selectSelectedAccount);
    while (!selectedClAppAccountUserGuid) {
        yield take([REHYDRATE, LA_ACTION.SELECT_ACCOUNT]);
        ({ selectedClAppAccountUserGuid } = yield select(selectSelectedAccount));
    }
}

export function* clapiInit() {
    console.log("Crux/Init -> sagas/Init -> clapiInit");

    // Guard: wait for the full init sequence (config + accounts) before firing
    // non-critical CLAPI calls. This prevents these calls from using stale
    // timeout defaults or racing ahead of session establishment.
    yield call(waitForInitComplete);

    yield fork(getRecentAddressSearches, getRecentAddressSearchesAction());
    yield fork(getRecentParcelSearches, getRecentParcelSearchesAction());
    if (isNZ) {
        yield fork(getRecentTitleSearches, getRecentTitleSearchesAction());
    }

    yield fork(getNewUserActivityFeedsSaga, getNewUserActivityFeedsAction());
}

export function* getClapiUserAndLdSegment() {
    console.log("Crux/Init -> sagas/Init -> getClapiUserAndLdSegment");

    // Guard: wait for full init (config loaded + accounts fetched) before calling
    // user/summary. This ensures timeouts are configured and session is validated.
    yield call(waitForInitComplete);

    yield call(getUserDetail); // populates clapi.usrDetail via UserPreferences.getUserSummary()

    // LD and Segment both need claud.user (populated by a separate GET_USER_DETAILS
    // dispatch from AuthenticatedRoutePing / CruxPageLayout).  That dispatch may have
    // already completed before we reach this point, so check the store first.
    let claudUser = yield select(selectClaudUser);
    while (!claudUser) {
        yield take([GET_USER_DETAILS_SUCCESS, GET_USER_DETAILS_FAIL]);
        claudUser = yield select(selectClaudUser);
    }

    // launchDarkly
    yield put({ type: LD_INIT });
    yield fork(initializeLD);
    yield fork(initApm);

    // segment init
    yield put({ type: SEGMENT_INIT });
    yield fork(initializeSegment);
}

export function* appInit() {
    console.log("Crux/Init -> sagas/Init -> appInit");

    yield fork(clapiInit);
    yield fork(getClapiUserAndLdSegment);
}

export default [
    appInit(),
];
